import React, { useCallback, useEffect, useRef, useState } from "react";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Button } from "@/modern/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { boardAlertSchema, BoardAlertFormData } from "@/react/schema/boardAlerts.schema";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { showToast, defaultCellRenderer, defaultGroupRenderer as groupRenderer } from "@/other/utils/utils";
import { BryntumWidget } from "@bryntum/schedulerpro-react";
import { Store } from "@bryntum/schedulerpro";
import { WIDGET_KEYS } from "@/other/constants/WidgetKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";

const DELIVERED_TEXT = {
  isEmailNotify: "Email",
  isWorkforceNotify: "Workforce",
  bannerNotify: "Banner"
};

interface BoardAlertsEditorProps {
  record: {
    reminderRecord?: any;
    defaultSelectedBoardUsers?: any[];
    isPhantom?: boolean;
    [key: string]: any;
  };
  onClose: () => void;
  editorInstance?: any;
  context?: any;
}

export const BoardAlertsEditor = ({ record, onClose, editorInstance }: BoardAlertsEditorProps) => {
  const { project } = useSwivuBryntumContext();
  const gridRef = useRef<any>(null);
  const [reminderRecordState, setReminderRecordState] = useState<any>(null);
  const remRecStoreDestroyListenerRef = useRef<any>(null);
  const isInitializedRef = useRef(false);
  const [isSendEnabled, setIsSendEnabled] = useState(false);

  const { reminderRecord, defaultSelectedBoardUsers = [] } = record;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<BoardAlertFormData>({
    resolver: zodResolver(boardAlertSchema),
    defaultValues: {
      title: reminderRecord?.title || "",
      description: reminderRecord?.description || ""
    }
  });

  const titleValue = watch("title");
  const descriptionValue = watch("description");

  const updateRecipientsCell = useCallback((recipientRecords: any[], reminderId: number) => {
    recipientRecords.forEach((recipientRecord) => {
      if (recipientRecord.reminderId === reminderId) {
        const channelsWhereAlertsSent = Object.keys(DELIVERED_TEXT)
          .reduce((accum: string[], value) => {
            if (recipientRecord[value as keyof typeof DELIVERED_TEXT]) {
              accum.push(DELIVERED_TEXT[value as keyof typeof DELIVERED_TEXT]);
            }
            return accum;
          }, [])
          .join(" | ");
        recipientRecord.boardUser.delivered = channelsWhereAlertsSent;
      }
    });
  }, []);

  const toggleSendButtonDisable = useCallback(() => {
    const grid = gridRef.current?.instance;
    if (!grid) return;

    const hasSelection = grid.selectedRecords?.length > 0;
    const hasTitle = titleValue?.trim();
    const hasDescription = descriptionValue?.trim();
    const isPhantom = reminderRecordState?.isPhantom;

    setIsSendEnabled(Boolean(hasSelection && hasTitle && hasDescription && isPhantom));
  }, [titleValue, descriptionValue, reminderRecordState]);

  // Initialize data
  useEffect(() => {
    if (!project || isInitializedRef.current || !gridRef.current?.instance) return;

    const grid = gridRef.current.instance;
    isInitializedRef.current = true;

    // Create a copy of the reminder record
    const recordCopy = reminderRecord?.copy
      ? reminderRecord.copy()
      : {
          ...reminderRecord,
          isPhantom: reminderRecord?.isPhantom ?? true
        };

    // Opening existing board alert record, empty the boardUserSeen
    if (!reminderRecord?.isPhantom) {
      recordCopy.boardUserSeen = [];
    }

    setReminderRecordState(recordCopy);

    // Get all board users and clear cached delivered value
    const boardUsers = project.boardUsersStore.query((rec: any) => {
      rec.delivered = null;
      return true;
    });

    // Create Bryntum store with board users
    grid.store = new Store({
      data: boardUsers
    });
    grid.store.sort("name", true);

    // Set initial selection
    if (defaultSelectedBoardUsers.length) {
      grid.selectedRecords = defaultSelectedBoardUsers;
    } else if (!reminderRecord?.isPhantom && reminderRecord?.recipients) {
      // For existing records, select recipients and display status
      grid.selectedRecords = reminderRecord.recipients.map((recipient: any) => recipient.boardUser.id);
      grid.selectedRecords.forEach((userRecord: any) => {
        updateRecipientsCell(userRecord.alertRecipients, reminderRecord.id);
      });
    }

    // Listen to recipient store updates
    if (project.reminderRecipientStore) {
      const listener = project.reminderRecipientStore.on({
        update({ records: recipientRecords }: { records: any[] }) {
          if (recordCopy?.id) {
            updateRecipientsCell(recipientRecords, recordCopy.id);
          }
        },
        // @ts-expect-error - Bryntum thisObj type
        thisObj: null
      });
      remRecStoreDestroyListenerRef.current = listener;
    }

    toggleSendButtonDisable();

    return () => {
      if (remRecStoreDestroyListenerRef.current) {
        remRecStoreDestroyListenerRef.current();
        remRecStoreDestroyListenerRef.current = null;
      }
    };
  }, [project, reminderRecord, defaultSelectedBoardUsers, updateRecipientsCell, toggleSendButtonDisable]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const grid = gridRef.current?.instance;
    if (!grid) return;

    const value = e.target.value;
    if (value) {
      grid.store.filter({
        property: "name",
        value,
        caseSensitive: false,
        exactMatch: false
      });
    } else {
      grid.store.clearFilters();
    }
  };

  const onGridSelectionChange = useCallback(() => {
    toggleSendButtonDisable();
  }, [toggleSendButtonDisable]);

  const onSendBoardAlert = (data: BoardAlertFormData) => {
    const grid = gridRef.current?.instance;
    if (!project || !reminderRecordState || !grid) return;

    const selectedRecords = grid.selectedRecords;
    if (selectedRecords.length === 0) return;

    // Update reminder record with form data
    reminderRecordState.title = data.title;
    reminderRecordState.description = data.description;

    // FIX: Create records first, then assign properties to avoid ID assignment issues
    //
    // BUG CONTEXT: When reminderRecipientStore is empty and records are added via server sync/websocket,
    // the createRecord() method with constructor arguments can cause ID assignment issues where
    // reminderId is not properly assigned to the new record.
    //
    // SOLUTION: Create the record first without arguments, then explicitly assign the properties
    // after the record is fully initialized. This ensures proper property assignment regardless
    // of the store's state or server sync timing.
    const reminderRecipients = selectedRecords.map((boardUser: any) => {
      const newRecord = project.reminderRecipientStore.createRecord({}) as any;
      newRecord.reminderId = reminderRecordState.id;
      newRecord.boardUserId = boardUser.id;
      return newRecord;
    });

    project.reminderStore.add(reminderRecordState);
    project.reminderRecipientStore.add(reminderRecipients);

    // Display temporary status for selected users
    selectedRecords.forEach((record: any) => {
      record.delivered = "Retrieving Sent Status...";
    });

    // @ts-expect-error - showToast type signature
    showToast({
      type: "success",
      msg: "Board Alert sent"
    });

    // Disable form after sending
    setReminderRecordState({ ...reminderRecordState, isPhantom: false });
    setIsSendEnabled(false);

    // Make grid read-only
    grid.readOnly = true;
  };

  // Watch form changes to toggle send button
  useEffect(() => {
    toggleSendButtonDisable();
  }, [titleValue, descriptionValue, toggleSendButtonDisable]);

  const gridConfig = {
    height: "100%",
    cls: "w-full",
    stateId: PERSISTENCE_STATE_KEYS.boardAlertsEditor,
    features: {
      cellMenu: false,
      sort: { multiSort: false }
    },
    selectionMode: { checkbox: true, checkboxOnly: true, showCheckAll: true },
    columns: [
      {
        text: "Board User",
        field: "name",
        editor: false,
        sortable: true,
        groupable: true,
        flex: 1
      },
      {
        id: "user-role",
        text: "Role",
        field: "role",
        displayField: "name",
        editor: false,
        sortable: true,
        groupable: true,
        flex: 1,
        groupRenderer({ groupRecords, groupRowFor, count }: any) {
          return groupRowFor ? groupRenderer({ groupRowFor: groupRecords[0].roleName, count }) : defaultCellRenderer({ value: null });
        },
        renderer: ({ record }: any) => {
          return record.roleName;
        }
      },
      {
        id: "delivered",
        text: "Delivered",
        field: "delivered",
        sortable: true,
        groupable: false,
        editor: false,
        flex: 1
      }
    ],
    listeners: {
      selectionChange: onGridSelectionChange
    }
  };

  return (
    <BaseModal
      record={record}
      onClose={onClose}
      title="Board Alert"
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "60em",
        height: "35em",
        maxHeight: "100vh",
        maxWidth: "100vw",
        minWidth: "40em",
        minHeight: "35em"
      }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
      footerRenderer={() => (
        <div className="flex gap-2 justify-end p-4">
          <Button
            variant="outline"
            onClick={handleSubmit(onSendBoardAlert)}
            disabled={!isSendEnabled || !reminderRecordState?.isPhantom}
            className="gap-2 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
          >
            <i className="fa-light fa-paper-plane-top" />
            Send Alert
          </Button>
          <Button variant="outline" onClick={onClose} className="gap-2">
            <i className="fa-light fa-close" />
            Close
          </Button>
        </div>
      )}
      footerClassName="!p-0"
    >
      <BaseModalBody className="!p-4 !gap-3 flex flex-col">
        <div className="flex flex-col gap-3">
          <Input {...register("title")} placeholder="Board Alert Title" className="w-full" disabled={!reminderRecordState?.isPhantom} />
          {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}

          <Textarea {...register("description")} placeholder="Board Alert Description" className="w-full min-h-[95px]" disabled={!reminderRecordState?.isPhantom} />
          {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}

          <div className="relative">
            <Input type="text" placeholder="Find a Board User" onChange={handleSearchChange} className="w-full" disabled={!reminderRecordState?.isPhantom} />
            <i className="fa fa-search absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {/* @ts-expect-error - Bryntum Widget types */}
          <BryntumWidget ref={gridRef} type={WIDGET_KEYS.swivuPreferenceStyleGrid} config={gridConfig} />
        </div>
      </BaseModalBody>
    </BaseModal>
  );
};
